import type {
  EntitySummary,
  EntityDetail,
  ReviewDTO,
  Paginated,
  PublicProfile,
  AdminQueueItem,
  TestimonialDTO,
} from "../../shared/api";
import type { EntityAnalytics, OwnedReview } from "../../worker/services/analytics";

export type { EntitySummary, EntityDetail, ReviewDTO, Paginated, PublicProfile, AdminQueueItem, TestimonialDTO };

const DEV_USER_KEY = "cc-dev-user";
const isBrowser = typeof window !== "undefined";

function readDevUser(): string | null {
  if (!isBrowser) return null;
  try {
    return localStorage.getItem(DEV_USER_KEY);
  } catch {
    return null;
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body && !headers.has("content-type")) headers.set("content-type", "application/json");
  // Dev convenience: impersonate via header when no real session exists yet.
  // Guarded because this module is reachable from the server bundle, which has
  // no localStorage.
  const devUser = readDevUser();
  if (devUser) headers.set("X-Dev-User", devUser);

  const res = await fetch(path, { ...init, headers, credentials: "include" });
  const text = await res.text();
  const json = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const err = json?.error ?? { code: "error", message: `Request failed (${res.status})` };
    throw Object.assign(new Error(err.message), { code: err.code, status: res.status, body: json?.error });
  }
  return json as T;
}

export interface ListingSettingsDTO {
  mode: "free" | "paid" | "both";
  currency: string;
  /** Minor units. null means the item is not priced here. */
  tokenListingCents: number | null;
  companyListingCents: number | null;
  managedCents: number | null;
  slaHours: number;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface MeResponse {
  user: {
    id: string;
    email: string;
    displayName: string | null;
    username: string | null;
    reputation: number;
    isStaff?: boolean;
  } | null;
  memberships: { orgId: string; role: string; status: string; slug: string; name: string }[];
  /** Which sign-in methods this deployment offers. Absent on an old cached response. */
  auth?: { sso: boolean; devSignIn: boolean };
}

export const api = {
  me: () => req<MeResponse>("/api/v1/me"),

  directory: (params: Record<string, string | undefined>) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]);
    return req<Paginated<EntitySummary>>(`/api/v1/entities?${qs}`);
  },

  entity: (type: string, slug: string) => req<EntityDetail>(`/api/v1/entities/${type}/${slug}`),

  testimonials: () => req<{ items: TestimonialDTO[] }>("/api/v1/reviews/testimonials"),

  submitListing: (body: {
    type: "token" | "company";
    name: string;
    symbol?: string | null;
    website: string;
    shortDesc: string;
    chain?: string | null;
    contractAddress?: string | null;
    priority?: boolean;
  }) =>
    req<{ ok: true; id: string; slug: string; checkoutUrl?: string | null }>("/api/v1/submissions", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  adminEntities: (query = "") =>
    req<{
      total: number;
      items: {
        id: string; type: string; slug: string; name: string; symbol: string | null;
        shortDesc: string | null; longDesc: string | null; website: string | null;
        status: string; claimStatus: string; source: string; reviewCount: number;
        ratingWeighted: number | null; trustScore: number | null;
        chain: string | null; contractAddress: string | null; coingeckoId: string | null;
        categoryIds: string[]; createdAt: string; updatedAt: string;
      }[];
    }>(`/api/v1/admin/entities${query ? `?${query}` : ""}`),

  adminCreateEntity: (body: {
    type: "token" | "company";
    name: string;
    symbol?: string | null;
    website: string;
    shortDesc: string;
    chain?: string | null;
    contractAddress?: string | null;
    categoryIds?: string[];
  }) => req<{ ok: true; id: string; slug: string }>("/api/v1/admin/entities", {
    method: "POST",
    body: JSON.stringify(body),
  }),

  adminUpdateEntity: (id: string, body: Record<string, unknown>) =>
    req<{ ok: true }>(`/api/v1/admin/entities/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  adminSetEntityStatus: (id: string, status: "published" | "unlisted") =>
    req<{ ok: true }>(`/api/v1/admin/entities/${encodeURIComponent(id)}/status`, {
      method: "POST",
      body: JSON.stringify({ status }),
    }),

  adminDeleteEntity: (id: string) =>
    req<{ ok: true }>(`/api/v1/admin/entities/${encodeURIComponent(id)}`, { method: "DELETE" }),

  adminApproveEntity: (id: string) =>
    req<{ ok: true }>(`/api/v1/admin/entities/${encodeURIComponent(id)}/approve`, { method: "POST" }),

  adminRejectEntity: (id: string, reason: string) =>
    req<{ ok: true }>(`/api/v1/admin/entities/${encodeURIComponent(id)}/reject`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  mySubmissions: () =>
    req<{
      items: { id: string; type: string; slug: string; name: string; status: string; createdAt: string }[];
      orders: { id: string; kind: string; status: string; createdAt: string }[];
    }>("/api/v1/submissions/mine"),

  submissionOptions: () =>
    req<{
      mode: "free" | "paid" | "both";
      freeAllowed: boolean;
      paidOffered: boolean;
      priorityAvailable: boolean;
      managedAvailable: boolean;
      slaHours: number;
      currency: string;
      /** Minor units, or null when that item is not purchasable here. */
      prices: { token: number | null; company: number | null; managed: number | null };
    }>("/api/v1/submissions/options"),

  requestManagedOnboarding: (notes: string) =>
    req<{ ok: true; orderId: string; checkoutUrl: string | null }>("/api/v1/submissions/managed", {
      method: "POST",
      body: JSON.stringify({ notes }),
    }),

  entityById: (id: string) => req<EntityDetail>(`/api/v1/entities/by-id/${encodeURIComponent(id)}`),

  reviews: (entityId: string, sort?: string, offset = 0) =>
    req<Paginated<ReviewDTO>>(
      `/api/v1/reviews?entityId=${encodeURIComponent(entityId)}&sort=${sort ?? "verified"}&offset=${offset}`,
    ),

  createReview: (body: {
    entityId: string;
    ratingOverall: number;
    title?: string;
    body?: string;
    dimensions?: { key: string; score: number }[];
    verificationToken?: string;
  }) => req<{ id: string; status: string; verificationLevel: string }>("/api/v1/reviews", { method: "POST", body: JSON.stringify(body) }),

  voteReview: (id: string, vote: "helpful" | "unhelpful") =>
    req<{ ok: boolean }>(`/api/v1/reviews/${id}/vote`, { method: "POST", body: JSON.stringify({ vote }) }),

  flagReview: (id: string, reason: string) =>
    req<{ ok: boolean }>(`/api/v1/reviews/${id}/flag`, { method: "POST", body: JSON.stringify({ reason }) }),

  search: (q: string, type?: string) =>
    req<{ items: EntitySummary[] }>(`/api/v1/search?q=${encodeURIComponent(q)}${type ? `&type=${type}` : ""}`),

  category: (slug: string) =>
    req<{ slug: string; name: string; description: string | null }>(
      `/api/v1/categories/${encodeURIComponent(slug)}`,
    ),

  categories: (type?: string) =>
    req<{ items: { slug: string; type: string; name: string; parentId: string | null; description: string | null }[] }>(
      `/api/v1/categories${type ? `?type=${type}` : ""}`,
    ),

  walletNonce: (entityId: string, address: string) =>
    req<{ nonce: string; message: string }>("/api/v1/verify/wallet/nonce", {
      method: "POST",
      body: JSON.stringify({ entityId, address }),
    }),

  walletVerify: (nonce: string, signature: string) =>
    req<{ verificationToken: string; level: string; badge: string }>("/api/v1/verify/wallet/verify", {
      method: "POST",
      body: JSON.stringify({ nonce, signature }),
    }),

  compare: (ids: string[]) =>
    req<{ entities: EntityDetail[] }>(`/api/v1/compare?ids=${encodeURIComponent(ids.join(","))}`),

  user: (username: string) =>
    req<PublicProfile>(`/api/v1/users/${encodeURIComponent(username)}`),

  adminListingSettings: () => req<ListingSettingsDTO>("/api/v1/admin/listing-settings"),

  adminSaveListingSettings: (body: Partial<ListingSettingsDTO>) =>
    req<ListingSettingsDTO>("/api/v1/admin/listing-settings", {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  adminQueue: () => req<Paginated<AdminQueueItem>>("/api/v1/admin/queue?limit=50"),

  adminPublish: (id: string) =>
    req<{ ok: boolean }>(`/api/v1/admin/reviews/${id}/publish`, { method: "POST" }),

  adminRemove: (id: string, reason: string) =>
    req<{ ok: boolean }>(`/api/v1/admin/reviews/${id}/remove`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  adminResolveFlag: (id: string, outcome: "upheld" | "rejected") =>
    req<{ ok: boolean }>(`/api/v1/admin/flags/${id}/resolve`, {
      method: "POST",
      body: JSON.stringify({ outcome }),
    }),

  // ---- Vendor portal ----
  dashEntities: () =>
    req<{ items: { id: string; type: string; slug: string; name: string; short_desc: string | null; claim_status: string; rating_weighted: number | null; review_count: number }[] }>(
      "/api/v1/dashboard/entities",
    ),

  dashUpdateEntity: (id: string, fields: Record<string, string | null>) =>
    req<{ ok: boolean; updated: string[] }>(`/api/v1/dashboard/entities/${id}`, {
      method: "PATCH",
      body: JSON.stringify(fields),
    }),

  claimable: (entityId: string) =>
    req<{ claimable: boolean; methods: string[]; reason?: string }>(
      `/api/v1/dashboard/claimable/${entityId}`,
    ),

  startClaim: (entityId: string, method: string) =>
    req<{ claimId: string; method: string; recordName: string; recordValue: string }>(
      "/api/v1/dashboard/claims",
      { method: "POST", body: JSON.stringify({ entityId, method }) },
    ),

  verifyClaim: (claimId: string, proof: { message?: string; signature?: string } = {}) =>
    req<{ status: string; orgId?: string }>(`/api/v1/dashboard/claims/${claimId}/verify`, {
      method: "POST",
      body: JSON.stringify(proof),
    }),

  dashMembers: () =>
    req<{ items: { username: string; display_name: string | null; role: string; status: string }[] }>(
      "/api/v1/dashboard/members",
    ),

  dashInvite: (email: string, role: string) =>
    req<{ invitationId: string; token: string }>("/api/v1/dashboard/members/invite", {
      method: "POST",
      body: JSON.stringify({ email, role }),
    }),

  respondToReview: (id: string, body: string) =>
    req<{ ok: boolean }>(`/api/v1/reviews/${id}/respond`, {
      method: "POST",
      body: JSON.stringify({ body }),
    }),

  disputeReview: (id: string, reason: string) =>
    req<{ ok: boolean; disputeId: string }>(`/api/v1/reviews/${id}/dispute`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  dashReviews: () =>
    req<{ items: OwnedReview[] }>("/api/v1/dashboard/reviews"),

  dashAnalytics: () =>
    req<{ items: EntityAnalytics[] }>("/api/v1/dashboard/analytics"),

  dashKeys: () =>
    req<{ items: { id: string; name: string; prefix: string | null; createdAt: string; lastUsedAt: string | null; revokedAt: string | null }[]; entitled: boolean }>(
      "/api/v1/dashboard/keys",
    ),

  dashCreateKey: (name: string) =>
    req<{ id: string; key: string; prefix: string }>("/api/v1/dashboard/keys", {
      method: "POST",
      body: JSON.stringify({ name }),
    }),

  dashRevokeKey: (id: string) =>
    req<{ ok: boolean }>(`/api/v1/dashboard/keys/${id}/revoke`, { method: "POST" }),

  dashBadgeSnippet: (entityId: string) =>
    req<{ imageUrl: string; snippet: string }>(`/api/v1/dashboard/badge-snippet/${entityId}`),

  featured: (params: { type?: string; category?: string }) => {
    const q = new URLSearchParams();
    if (params.type) q.set("type", params.type);
    if (params.category) q.set("category", params.category);
    return req<{ items: EntitySummary[]; disclosure: string }>(`/api/v1/featured?${q}`);
  },

  adminResolveCluster: (id: string, outcome: "confirmed" | "dismissed") =>
    req<{ ok: boolean }>(`/api/v1/admin/clusters/${id}/resolve`, {
      method: "POST",
      body: JSON.stringify({ outcome }),
    }),

  setDevUser: (handle: string | null) => {
    if (!isBrowser) return;
    try {
      if (handle) localStorage.setItem(DEV_USER_KEY, handle);
      else localStorage.removeItem(DEV_USER_KEY);
    } catch {
      // Storage unavailable — dev impersonation simply does not persist.
    }
  },
  getDevUser: () => readDevUser(),
};
